<template>
  <div id="wa-growth-dashboard" class="wag-dashboard">
    <!-- KPI Cards -->
    <div class="stats-grid" style="grid-template-columns: repeat(3, 1fr)">
      <!-- Total Tickets -->
      <div class="stat-card">
        <div class="stat-label">🎟 Total Tickets (vs 30d Avg)</div>
        <div class="stat-row">
          <span class="stat-value">{{ fmt(data.summary.total) }}</span>
          <span class="stat-sub">avg {{ fmt(data.summary.dailyAvg) }}/day</span>
        </div>
      </div>

      <!-- New Customer -->
      <div class="stat-card" v-if="newCustomer">
        <div class="stat-label">🆕 New Customer</div>
        <div class="stat-row">
          <span class="stat-value" style="color: var(--accent-green)">{{ fmt(newCustomer.total) }}</span>
          <span class="stat-sub">avg {{ fmt(newCustomer.dailyAvg) }}/day</span>
        </div>
      </div>

      <!-- Returning Customer -->
      <div class="stat-card" v-if="returningCustomer">
        <div class="stat-label">🔁 Returning Customer</div>
        <div class="stat-row">
          <span class="stat-value" style="color: var(--accent-purple)">{{ fmt(returningCustomer.total) }}</span>
          <span class="stat-sub">avg {{ fmt(returningCustomer.dailyAvg) }}/day</span>
        </div>
      </div>
    </div>

    <!-- Ticket Breakdown + Daily Trend (combined) -->
    <div class="chart-box">
      <div class="chart-title">Ticket Breakdown & Daily Trend</div>

      <div
        v-for="m in data.metrics"
        :key="m.key"
        class="wag-metric-section"
      >
        <!-- Bar row: icon + label + bar + stats -->
        <div class="wt-source-row">
          <div class="wt-source-label">
            <span class="wt-source-icon">{{ m.icon }}</span>
            <span>{{ m.label }}</span>
          </div>
          <div class="wt-source-bar-track">
            <div
              class="wt-source-bar-fill"
              :style="{ width: barWidth(m.total) + '%', background: metricColor(m.key) }"
            ></div>
          </div>
          <div class="wt-source-stats">
            <span class="wt-source-total">{{ fmt(m.total) }}</span>
            <span class="wt-source-detail">avg {{ fmt(m.dailyAvg) }}/day</span>
          </div>
        </div>

        <!-- Daily trend cells -->
        <div v-if="m.days.length" class="wag-trend-row">
          <div
            v-for="day in m.days"
            :key="day.label"
            class="wag-trend-cell"
            :class="'wag-trend--' + day.trend"
          >
            <div class="wag-trend-day">{{ formatDay(day.label) }}</div>
            <div class="wag-trend-val">{{ day.value }}</div>
            <div class="wag-trend-avg">/ {{ day.rollingAvg }}</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'

const props = defineProps({
  data: { type: Object, required: true },
})

const newCustomer = computed(() =>
  props.data.sources.find(s => s.key === 'new-customer') || null
)
const returningCustomer = computed(() =>
  props.data.sources.find(s => s.key === 'returning-customer') || null
)

const maxTotal = computed(() =>
  Math.max(...props.data.metrics.map(m => m.total), 1)
)

function barWidth(total) {
  return Math.max((total / maxTotal.value) * 100, 1)
}

function fmt(n) {
  return n.toLocaleString('id-ID')
}

const METRIC_COLORS = {
  'total-tickets': 'var(--accent-blue)',
  'new-customer': 'var(--accent-green)',
  'returning-customer': 'var(--accent-purple)',
  'sales-hunt': 'var(--accent-yellow)',
  'sales-followup': 'var(--accent-red)',
}

function metricColor(key) {
  return METRIC_COLORS[key] || 'var(--text-dim)'
}

function formatDay(label) {
  return label.replace(/^(W\d)/, '$1 ')
}
</script>

<style>
.wag-dashboard {
  margin-bottom: 20px;
}

.wag-metric-section {
  margin-bottom: 14px;
  padding-bottom: 14px;
  border-bottom: 1px solid var(--border);
}

.wag-metric-section:last-child {
  margin-bottom: 0;
  padding-bottom: 0;
  border-bottom: none;
}

.wag-metric-section .wt-source-row {
  margin-bottom: 8px;
}

.wag-trend-row {
  display: flex;
  gap: 4px;
  overflow-x: auto;
  padding-bottom: 4px;
}

.wag-trend-cell {
  flex: 0 0 auto;
  min-width: 52px;
  padding: 6px 8px;
  border-radius: 6px;
  border-left: 3px solid transparent;
  background: rgba(232, 200, 124, 0.04);
  text-align: center;
  transition: 0.15s;
}

.wag-trend-cell:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
}

.wag-trend--up {
  border-left-color: var(--accent-green);
  background: rgba(143, 174, 91, 0.08);
}

.wag-trend--down {
  border-left-color: var(--accent-red);
  background: rgba(192, 67, 46, 0.08);
}

.wag-trend--flat {
  border-left-color: var(--accent-yellow);
  background: rgba(232, 200, 124, 0.08);
}

.wag-trend-day {
  font-size: 9px;
  color: var(--text-dim);
  font-weight: 600;
  letter-spacing: 0.5px;
  margin-bottom: 2px;
}

.wag-trend-val {
  font-size: 14px;
  font-weight: 800;
  color: var(--text-primary);
  font-family: var(--sans);
}

.wag-trend--up .wag-trend-val { color: var(--accent-green); }
.wag-trend--down .wag-trend-val { color: var(--accent-red); }
.wag-trend--flat .wag-trend-val { color: var(--accent-yellow); }

.wag-trend-avg {
  font-size: 9px;
  color: var(--text-faint);
  margin-top: 1px;
}

@media (max-width: 768px) {
  .wag-trend-cell {
    min-width: 44px;
    padding: 4px 6px;
  }
  .wag-trend-val {
    font-size: 12px;
  }
}
</style>
